import { NavLink } from "react-router-dom";
import { useFilters } from "@/context/FilterContext";
import {
  TrainFront, LayoutDashboard, Activity, Wrench, ShieldCheck, BarChart3,
  TrendingUp, AlertTriangle, Layers, Search, FileText, Settings as SettingsIcon,
  RefreshCw, Filter, Target, Zap, Building2
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Overview", icon: LayoutDashboard },
  { to: "/reliability", label: "Reliability", icon: Activity },
  { to: "/availability", label: "Availability", icon: ShieldCheck },
  { to: "/maintainability", label: "Maintainability", icon: Wrench },
  { to: "/safety", label: "Safety", icon: ShieldCheck },
  { to: "/pareto", label: "Pareto Analysis", icon: BarChart3 },
  { to: "/trend", label: "Trend Analysis", icon: TrendingUp },
  { to: "/failure", label: "Failure Analysis", icon: AlertTriangle },
  { to: "/subsystem", label: "Subsystem Analysis", icon: Layers },
  { to: "/events", label: "Event Explorer", icon: Search },
  { to: "/reports", label: "Reports", icon: FileText },
  { to: "/spm", label: "SPM", icon: Target },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

const POWER_NAV = [
  { to: "/power/tss", label: "TSS", icon: Zap },
  { to: "/power/rss", label: "RSS", icon: Building2 },
];

export default function Sidebar() {
  const { start, end, line, subsystem, setStart, setEnd, setLine, setSubsystem, meta, reset, refreshMeta } = useFilters();

  return (
    <aside
      data-testid="app-sidebar"
      className="w-[260px] h-screen fixed left-0 top-0 bg-[#0f172a] text-slate-300 flex flex-col overflow-y-auto trams-scroll z-40"
    >
      {/* Logo */}
      <div className="px-6 pt-6 pb-4 flex items-center gap-3 border-b border-white/5">
        <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white">
          <TrainFront className="w-6 h-6" />
        </div>
        <div>
          <div className="text-lg font-black text-white tracking-tight leading-none">TRAMS</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">RAMS Dashboard</div>
        </div>
      </div>

      {/* Nav */}
      <div className="px-4 pt-4">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 px-3 mb-2">
          RAMS Operational
        </div>
        <nav className="space-y-0.5">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#1e40af] text-white shadow-sm"
                    : "text-slate-400 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 px-3 mt-5 mb-2">
          Power Consumption
        </div>
        <nav className="space-y-0.5">
          {POWER_NAV.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-power-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#1e40af] text-white shadow-sm"
                    : "text-slate-400 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              {label}
            </NavLink>
          ))}
        </nav>
      </div>

      {/* Filter Panel */}
      <div className="px-4 mt-6 pb-4 flex-1">
        <div className="flex items-center gap-2 mb-3 px-3">
          <Filter className="w-3.5 h-3.5 text-slate-400" />
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Filter Panel</div>
        </div>

        <div className="space-y-3 px-1">
          <div>
            <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-500 mb-1.5 px-2">Date Range</div>
            <div className="space-y-1.5">
              <Input
                type="date"
                value={start || ""}
                onChange={(e) => setStart(e.target.value)}
                data-testid="filter-start-date"
                className="h-9 bg-white/5 border-white/10 text-white text-xs [color-scheme:dark]"
              />
              <Input
                type="date"
                value={end || ""}
                onChange={(e) => setEnd(e.target.value)}
                data-testid="filter-end-date"
                className="h-9 bg-white/5 border-white/10 text-white text-xs [color-scheme:dark]"
              />
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-500 mb-1.5 px-2">Line / Train</div>
            <Select value={line} onValueChange={setLine}>
              <SelectTrigger data-testid="filter-line" className="h-9 bg-white/5 border-white/10 text-white text-xs">
                <SelectValue placeholder="All Lines" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Lines</SelectItem>
                {meta.lines.map((l) => (
                  <SelectItem key={l} value={l}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-500 mb-1.5 px-2">Subsystem</div>
            <Select value={subsystem} onValueChange={setSubsystem}>
              <SelectTrigger data-testid="filter-subsystem" className="h-9 bg-white/5 border-white/10 text-white text-xs">
                <SelectValue placeholder="All Subsystems" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Subsystems</SelectItem>
                {meta.subsystems.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            onClick={reset}
            data-testid="filter-reset-btn"
            className="w-full h-9 bg-[#1e40af] hover:bg-[#1e3a8a] text-white text-xs font-semibold"
          >
            <Filter className="w-3.5 h-3.5 mr-1.5" />
            Reset Filter
          </Button>
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-white/5 text-[10px] text-slate-500 flex items-center justify-between">
        <div>
          <div className="uppercase tracking-widest text-slate-600 text-[9px]">Data last updated</div>
          <div className="text-slate-400 mt-0.5">{meta.last_updated ? new Date(meta.last_updated).toLocaleString() : "—"}</div>
        </div>
        <button
          onClick={refreshMeta}
          data-testid="sidebar-refresh"
          className="text-slate-500 hover:text-white p-1.5 rounded transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>
    </aside>
  );
}
